import React, { useEffect, useMemo, useState } from 'react';
import { constructMediaUrl } from '../../api/helpers';
import { getFileType, isTransientOutputUrl } from '../../api/structs/clip';
import { ImageGallery, VideoGallery } from './output';
import ClipPlayer from './ClipPlayer';
import SlideOutputStrip from './output/SlideOutputStrip';
import { clipStyleLabel } from './clipStyleDisplay';

interface OutputGalleryProps {
  fileUrls: string[];
  clipStyle: string;
}

const OutputGallery: React.FC<OutputGalleryProps> = ({
  fileUrls,
  clipStyle,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const stableUrls = useMemo(() => {
    const seen = new Set<string>();
    return (fileUrls || [])
      .map((url) => (url || '').trim())
      .filter((url) => {
        if (!url) return false;
        if (isTransientOutputUrl(url)) return false;
        if (seen.has(url)) return false;
        seen.add(url);
        return true;
      });
  }, [fileUrls]);

  const inferredPrimaryType =
    stableUrls.map((url) => getFileType(url)).find((type) => type !== 'unknown') || 'unknown';
  const primaryType = inferredPrimaryType;
  const normalizedUrls =
    primaryType === 'unknown'
      ? stableUrls
      : stableUrls.filter((url) => {
          const type = getFileType(url);
          return type === primaryType || type === 'unknown';
        });
  const safeUrls = normalizedUrls.length > 0 ? normalizedUrls : stableUrls;

  useEffect(() => {
    setCurrentIndex((previous) => {
      if (safeUrls.length === 0) return 0;
      return Math.max(0, Math.min(previous, safeUrls.length - 1));
    });
  }, [safeUrls.length]);

  if (safeUrls.length === 0) {
    return null;
  }

  const renderSurface = (title: string, content: React.ReactNode) => (
    <div className="mt-4 overflow-hidden rounded-lg border border-white/10 bg-black">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2.5">
        <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-400 font-mono">{title}</p>
        <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-600 font-mono">{clipStyleLabel(clipStyle)}</span>
      </div>
      <div className="p-3">{content}</div>
    </div>
  );

  // Only a carousel render mixes stills and per-slide videos; keeping the first
  // file type (below) hid every clip behind the stills.
  const hasStills = stableUrls.some((url) => getFileType(url) === 'image');
  const hasVideos = stableUrls.some((url) => getFileType(url) === 'video');
  if (hasStills && hasVideos) {
    return renderSurface('Rendered Slides', <SlideOutputStrip urls={stableUrls} />);
  }

  if (primaryType === 'video' && safeUrls.length === 1) {
    return renderSurface('Rendered Video', <ClipPlayer clipUrl={constructMediaUrl(safeUrls[0])} />);
  }

  if (primaryType === 'video') {
    return renderSurface(
      'Rendered Videos',
      <VideoGallery
        urls={safeUrls}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
      />
    );
  }

  if (primaryType === 'image') {
    return renderSurface(
      'Output Gallery',
      <ImageGallery
        urls={safeUrls}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
      />
    );
  }

  return null;
};

export default OutputGallery;
